import {
  type IssueSortingDtoOptions,
  type IssueSubtypeDto,
  type IssueTypeDto,
} from '@issuemanager/api-client';
import {
  ActionStatusData,
  getSortingModelByField,
} from '@issuemanager/apps/client/components';
import { IssueStatusData } from '@issuemanager/client/features/issues';
import {
  ISSUE_FIELD_ACTION_APPROVER,
  ISSUE_FIELD_ACTION_COUNT,
  ISSUE_FIELD_ACTION_CREATED,
  ISSUE_FIELD_ACTION_DUE_DATE,
  ISSUE_FIELD_ACTION_OWNER,
  ISSUE_FIELD_ACTION_STATUS,
  ISSUE_FIELD_ACTION_TITLE,
  ISSUE_FIELD_ACTUAL_REMEDIATION_DATE,
  ISSUE_FIELD_APPROVER,
  ISSUE_FIELD_CODE,
  ISSUE_FIELD_CREATED,
  ISSUE_FIELD_CREATOR,
  ISSUE_FIELD_DUE_DATE,
  ISSUE_FIELD_ENTITY_NAME,
  ISSUE_FIELD_ISSUE_STATUS,
  ISSUE_FIELD_ISSUE_SUBTYPE,
  ISSUE_FIELD_ISSUE_TYPE,
  ISSUE_FIELD_MANAGER,
  ISSUE_FIELD_OWNER,
  ISSUE_FIELD_RELATIONSHIP_COUNT,
  ISSUE_FIELD_SEVERITY,
  ISSUE_FIELD_SLA_DUE_DAYS,
  ISSUE_FIELD_SLA_STATUS,
  ISSUE_FIELD_SOURCE_APP,
  ISSUE_FIELD_TITLE,
  ISSUE_FIELD_UPDATED,
} from '@issuemanager/constants';
import type {
  GridColumnVisibilityModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  assignFilterParam,
  type ExtendedIssueFilteringDtoOptions,
  type IssueFilterDateFieldMap,
  type IssueGridFilterModel,
} from './types';

const dateFieldsMap: IssueFilterDateFieldMap = {
  [ISSUE_FIELD_CREATED]: ['createdAtRangeStart', 'createdAtRangeEnd'],
  [ISSUE_FIELD_UPDATED]: ['updatedAtRangeStart', 'updatedAtRangeEnd'],
  [ISSUE_FIELD_DUE_DATE]: ['dueDateRangeStart', 'dueDateRangeEnd'],
  [ISSUE_FIELD_ACTUAL_REMEDIATION_DATE]: [
    'actualRemediationDateRangeStart',
    'actualRemediationDateRangeEnd',
  ],
  [ISSUE_FIELD_ACTION_CREATED]: [
    'actionCreatedAtRangeStart',
    'actionCreatedAtRangeEnd',
  ],
  [ISSUE_FIELD_ACTION_DUE_DATE]: [
    'actionDueDateRangeStart',
    'actionDueDateRangeEnd',
  ],
};

export function useIssueGridQueryParams(
  filterModel: IssueGridFilterModel,
  sortModel: GridSortModel,
  columnVisibilityModel: GridColumnVisibilityModel,
): {
  filter: ExtendedIssueFilteringDtoOptions;
  sort: IssueSortingDtoOptions;
} {
  return {
    filter: useIssueGridQueryFilterParam(filterModel, columnVisibilityModel),
    sort: useIssueGridQuerySortParam(sortModel, columnVisibilityModel),
  };
}

function useIssueGridQueryFilterParam(
  filterModel: IssueGridFilterModel,
  columnVisibilityModel: GridColumnVisibilityModel,
): ExtendedIssueFilteringDtoOptions {
  const codeFilter = filterModel[ISSUE_FIELD_CODE];
  const titleFilter = filterModel[ISSUE_FIELD_TITLE];
  const issueStatusFilter = filterModel[ISSUE_FIELD_ISSUE_STATUS];
  const actionStatusFilter = filterModel[ISSUE_FIELD_ACTION_STATUS];
  const sourceAppFilter = filterModel[ISSUE_FIELD_SOURCE_APP];
  const sourceEntityNameFilter = filterModel[ISSUE_FIELD_ENTITY_NAME];
  const severityFilter = filterModel[ISSUE_FIELD_SEVERITY];
  const issueTypeFilter = filterModel[ISSUE_FIELD_ISSUE_TYPE];
  const issueSubtypeFilter = filterModel[ISSUE_FIELD_ISSUE_SUBTYPE];
  const createdAtFilter = filterModel[ISSUE_FIELD_CREATED];
  const actionCreatedAtFilter = filterModel[ISSUE_FIELD_ACTION_CREATED];
  const updatedAtFilter = filterModel[ISSUE_FIELD_UPDATED];
  const dueDateFilter = filterModel[ISSUE_FIELD_DUE_DATE];
  const actualRemediationDateFilter =
    filterModel[ISSUE_FIELD_ACTUAL_REMEDIATION_DATE];
  const actionDueDateFilter = filterModel[ISSUE_FIELD_ACTION_DUE_DATE];
  const relationshipCountFilter = filterModel[ISSUE_FIELD_RELATIONSHIP_COUNT];
  const actionOwnerFilter = filterModel[ISSUE_FIELD_ACTION_OWNER];
  const actionTitleFilter = filterModel[ISSUE_FIELD_ACTION_TITLE];
  const ownerFilter = filterModel[ISSUE_FIELD_OWNER];
  const approverFilter = filterModel[ISSUE_FIELD_APPROVER];
  const actionApproverFilter = filterModel[ISSUE_FIELD_ACTION_APPROVER];
  const creatorFilter = filterModel[ISSUE_FIELD_CREATOR];
  const slaStatus = filterModel[ISSUE_FIELD_SLA_STATUS];

  const code = codeFilter?.map((issue) => issue.code);
  const title = titleFilter?.map((issue) => issue.title);
  const status = issueStatusFilter?.map(
    (s) => (s as IssueStatusData).statusValue,
  );
  const actionStatus = actionStatusFilter?.map(
    (s) => (s as ActionStatusData).value,
  );

  const actionTitle = actionTitleFilter?.map((action) => action.title);
  const sourceApplication = sourceAppFilter?.map(
    (sourceApp) => sourceApp.value,
  );
  const sourceEntityName = sourceEntityNameFilter?.map(
    (entityName: { name: string }) => entityName.name,
  );

  const severity = severityFilter?.map((status) => {
    if (status.value === null) {
      return 'null';
    }
    return status.value;
  });
  const issueType = issueTypeFilter
    ?.map((issueType: IssueTypeDto) => issueType.name)
    .filter(Boolean) as string[];
  const issueSubtype = issueSubtypeFilter
    ?.map((issueSubtype: IssueSubtypeDto) => issueSubtype.id)
    .filter(Boolean) as string[];
  const slaDueDays = undefined; // Temporary disabled on frontend, due API changes (will do in SC)
  const actionCount = undefined; // Temporary disabled on frontend, due API changes (will do in SC)
  const relationshipCount = relationshipCountFilter?.map(
    (rc) => rc.relationshipCount,
  );
  const owner = ownerFilter?.map((owner) => owner.id);
  const actionOwner = actionOwnerFilter?.map((actionOwner) => actionOwner.id);
  const approver = approverFilter?.map((approver) => approver.id);
  const actionApprover = actionApproverFilter?.map(
    (actionApprover) => actionApprover.id,
  );
  const creator = creatorFilter?.map((creator) => creator.id);

  const createdAtRangeStart = createdAtFilter?.[0]?.toISOString();
  const createdAtRangeEnd = createdAtFilter?.[1]?.toISOString();
  const updatedAtRangeStart = updatedAtFilter?.[0]?.toISOString();
  const updatedAtRangeEnd = updatedAtFilter?.[1]?.toISOString();
  const dueDateRangeStart = dueDateFilter?.[0]?.toISOString();
  const dueDateRangeEnd = dueDateFilter?.[1]?.toISOString();
  const actualRemediationDateRangeStart =
    actualRemediationDateFilter?.[0]?.toISOString();
  const actualRemediationDateRangeEnd =
    actualRemediationDateFilter?.[1]?.toISOString();

  const actionDueDateRangeStart = actionDueDateFilter?.[0]?.toISOString();
  const actionDueDateRangeEnd = actionDueDateFilter?.[1]?.toISOString();
  const actionCreatedAtRangeStart = actionCreatedAtFilter?.[0]?.toISOString();
  const actionCreatedAtRangeEnd = actionCreatedAtFilter?.[1]?.toISOString();

  const queryFilterParam: ExtendedIssueFilteringDtoOptions = {
    code,
    title,
    status,
    actionStatus,
    sourceApplication,
    sourceEntityName,
    severity,
    issueType,
    issueSubtype,
    createdAtRangeStart,
    createdAtRangeEnd,
    updatedAtRangeStart,
    updatedAtRangeEnd,
    dueDateRangeStart,
    dueDateRangeEnd,
    actualRemediationDateRangeStart,
    actualRemediationDateRangeEnd,
    actionCreatedAtRangeStart,
    actionCreatedAtRangeEnd,
    actionDueDateRangeStart,
    actionDueDateRangeEnd,
    slaDueDays,
    actionCount,
    relationshipCount,
    actionOwner,
    actionApprover,
    actionTitle,
    owner,
    approver,
    creator,
    slaStatus,
  };

  const queryFilterParamWithVisibility: ExtendedIssueFilteringDtoOptions = {};

  (
    Object.keys(queryFilterParam) as (keyof ExtendedIssueFilteringDtoOptions)[]
  ).forEach((key) => {
    const normalizedKey = stripRangeKey(key);
    const isDateFieldHidden = Object.entries(dateFieldsMap).some(
      ([field, [startKey, endKey]]) =>
        (key === startKey || key === endKey) &&
        columnVisibilityModel[field] === false,
    );

    if (
      !isDateFieldHidden &&
      (!(normalizedKey in columnVisibilityModel) ||
        columnVisibilityModel[normalizedKey])
    ) {
      assignFilterParam(
        queryFilterParamWithVisibility,
        key,
        queryFilterParam[key],
      );
    }
  });
  return queryFilterParamWithVisibility;
}

function useIssueGridQuerySortParam(
  sortModel: GridSortModel,
  columnVisibilityModel: GridColumnVisibilityModel,
): IssueSortingDtoOptions {
  const code = getSortingModelByField(sortModel, ISSUE_FIELD_CODE);
  const title = getSortingModelByField(sortModel, ISSUE_FIELD_TITLE);
  const issueType = getSortingModelByField(sortModel, ISSUE_FIELD_ISSUE_TYPE);
  const issueSubtype = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_ISSUE_SUBTYPE,
  );
  const status = getSortingModelByField(sortModel, ISSUE_FIELD_ISSUE_STATUS);
  const sourceApplication = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_SOURCE_APP,
  );
  const sourceEntityName = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_ENTITY_NAME,
  );
  const severity = getSortingModelByField(sortModel, ISSUE_FIELD_SEVERITY);
  const createdAt = getSortingModelByField(sortModel, ISSUE_FIELD_CREATED);
  const updatedAt = getSortingModelByField(sortModel, ISSUE_FIELD_UPDATED);
  const dueDate = getSortingModelByField(sortModel, ISSUE_FIELD_DUE_DATE);
  const actualRemediationDate = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_ACTUAL_REMEDIATION_DATE,
  );
  const owner = getSortingModelByField(sortModel, ISSUE_FIELD_OWNER);
  const creator = getSortingModelByField(sortModel, ISSUE_FIELD_CREATOR);
  const manager = getSortingModelByField(sortModel, ISSUE_FIELD_MANAGER);
  const approver = getSortingModelByField(sortModel, ISSUE_FIELD_APPROVER);
  const slaDueDays = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_SLA_DUE_DAYS,
  );
  const actionCount = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_ACTION_COUNT,
  );
  const relationshipCount = getSortingModelByField(
    sortModel,
    ISSUE_FIELD_RELATIONSHIP_COUNT,
  );
  const querySortParam: IssueSortingDtoOptions = {
    code,
    title,
    issueType,
    issueSubtype,
    createdAt,
    updatedAt,
    dueDate,
    actualRemediationDate,
    status,
    sourceApplication,
    sourceEntityName,
    severity,
    owner,
    creator,
    manager,
    slaDueDays,
    actionCount,
    relationshipCount,
    approver,
  };

  return Object.keys(querySortParam).reduce((pre, cur) => {
    const key = cur as keyof IssueSortingDtoOptions;
    if (!(key in columnVisibilityModel) || columnVisibilityModel[key]) {
      // eslint-disable-next-line no-param-reassign
      pre[key] = querySortParam[key];
    }
    return pre;
  }, {} as IssueSortingDtoOptions);
}

const stripRangeKey = (key: string) =>
  key.replace(/(RangeStart|RangeEnd)$/, '');
